import { useState, useEffect, useCallback } from 'react';
import { useTranslation as useTranslationBase } from 'react-i18next';
import { ModuleName } from '@/lib/i18n/config';
import { safeReactChild } from '@/lib/utils';
import { useSafeTranslation } from '@/hooks/useSafeContent';

/**
 * Hook for module-specific translations (isolated, no common fallback)
 * @param moduleName - The name of the translation module
 * @returns Translation function scoped to the module
 */
export function useTranslation(moduleName: ModuleName) {
  try {
    const { t: moduleT, i18n, ready } = useTranslationBase(moduleName);
    const { safeT } = useSafeTranslation();
    const [isClient, setIsClient] = useState(false);
    const [namespaceLoaded, setNamespaceLoaded] = useState(ready);

    useEffect(() => {
      setIsClient(true);
    }, []);

    // Automatically load namespace if not ready and on client
    useEffect(() => {
      if (!isClient || !i18n) return;

      // Common namespaces to always load (shared across all modules)
      const commonNamespaces = [
        'common',
        'common-errors',
        'common-actions',
        'common-validation',
        'common-messages',
        'common-ui',
        'forms',
        'languages',
        'nav',
        'analytics',
        'inventory',
        'dimensions',
        'ingredients',
        'products-new'
      ];

      // Namespaces to load: module namespace + all common namespaces
      const namespacesToLoad = [moduleName, ...commonNamespaces];

      // Load all required namespaces
      const loadNamespaces = async () => {
        for (const ns of namespacesToLoad) {
          if (!i18n.hasLoadedNamespace(ns)) {
            try {
              await i18n.loadNamespaces(ns);
            } catch (err) {
              console.error(`Failed to load namespace: ${ns}`, err);
            }
          }
        }
        setNamespaceLoaded(true);

        // Emit event to trigger re-renders
        if (typeof document !== 'undefined') {
          const event = new CustomEvent('i18nNamespaceLoaded', {
            detail: { namespace: moduleName }
          });
          document.dispatchEvent(event);
        }
      };

      // Load namespaces
      if (!ready) {
        loadNamespaces().catch((err) => {
          console.error(`Failed to load namespaces for module: ${moduleName}`, err);
        });
      } else if (ready && !namespaceLoaded) {
        setNamespaceLoaded(true);
      }
    }, [isClient, ready, moduleName, i18n]);

    // Helper function to convert translation keys to readable text
    const getHumanReadableFallback = (key: string, context?: string, options?: Record<string, any>): string => {
      // IMPORTANT: Always use 'en' during SSR to prevent hydration mismatches
      // On client-side, always use 'en' as fallback to prevent wrong-language text showing
      // This ensures that if i18n state is inconsistent during language switch, we don't show Arabic in English UI
      const currentLang = 'en';

      // Extract the actual key without namespace prefix
      const actualKey = key.split('.').pop() || key;

      // Handle specific common keys for better UX during loading
      // Note: moduleName is typed as ModuleName, but context can be 'common'
      if (context === 'common') {
        // Handle nested keys like app.version, actions.search
        const fullKey = key; // Keep the full key for pattern matching

        if (currentLang === 'ar') {
          // Nested key patterns
          if (fullKey === 'app.version' || actualKey === 'version') return 'JOYOUS Admin v1.0.0';
          if (fullKey === 'actions.search' || fullKey === 'search') return 'بحث';
          if (fullKey === 'auth.logout' || actualKey === 'logout') return 'تسجيل الخروج';
          // Direct keys - Sidebar & Header common translations
          if (actualKey === 'collapseSidebar') return 'طي الشريط الجانبي';
          if (actualKey === 'expandSidebar') return 'توسيع الشريط الجانبي';
          if (actualKey === 'back') return 'رجوع';
          if (actualKey === 'profile') return 'الملف الشخصي';
          if (actualKey === 'name') return 'الاسم';
          if (actualKey === 'logout') return 'تسجيل الخروج';
        } else {
          // Nested key patterns
          if (fullKey === 'app.version' || actualKey === 'version') return 'JOYOUS Admin v1.0.0';
          if (fullKey === 'actions.search' || fullKey === 'search') return 'Search';
          if (fullKey === 'auth.logout' || actualKey === 'logout') return 'Logout';
          // Direct keys - Sidebar & Header common translations
          if (actualKey === 'collapseSidebar') return 'Collapse Sidebar';
          if (actualKey === 'expandSidebar') return 'Expand Sidebar';
          if (actualKey === 'back') return 'Back';
          if (actualKey === 'profile') return 'Profile';
          if (actualKey === 'name') return 'Name';
          if (actualKey === 'logout') return 'Logout';
        }
      }

      // Handle common keys across modules
      if (currentLang === 'ar') {
        if (key.includes('noData') || key.includes('empty')) return 'لا توجد بيانات';
        if (key.includes('loading')) return 'جاري التحميل';
        if (key.includes('error')) return 'خطأ';
        if (key.includes('save')) return 'حفظ';
        if (key.includes('cancel')) return 'إلغاء';
        if (key.includes('delete')) return 'حذف';
        if (key.includes('edit')) return 'تعديل';
        if (key.includes('add')) return 'إضافة';
        if (key.includes('search')) return 'بحث';
        if (key.includes('filter')) return 'تصفية';
        if (key.includes('sort')) return 'ترتيب';
      } else {
        if (key.includes('noData') || key.includes('empty')) return 'No data available';
        if (key.includes('loading')) return 'Loading...';
        if (key.includes('error')) return 'Error';
        if (key.includes('save')) return 'Save';
        if (key.includes('cancel')) return 'Cancel';
        if (key.includes('delete')) return 'Delete';
        if (key.includes('edit')) return 'Edit';
        if (key.includes('add')) return 'Add';
        if (key.includes('search')) return 'Search';
        if (key.includes('filter')) return 'Filter';
        if (key.includes('sort')) return 'Sort';
      }

      // Generic fallback: convert camelCase to Title Case
      const lastPart = key.split('.').pop() || key;
      let result = lastPart
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      // Apply interpolation if options provided
      if (options) {
        result = result.replace(/\{\{?(\w+)\}?\}/g, (_, k) => String(options[k] ?? ''));
      }
      return result;
    };
  
    // Get translation function that tries common namespace as fallback
    const getCommonTranslation = useCallback((key: string, options?: any): string | null => {
      if (!i18n || typeof i18n.t !== 'function') return null;
      try {
        // Try multiple fallback namespaces in order
        const fallbackNamespaces = [
          'forms',
          'products-new',
          'languages',
          'nav',
          'analytics',
          'inventory',
          'dimensions',
          'ingredients',
          'common'
        ];
        for (const ns of fallbackNamespaces) {
          const result = i18n.t(key, { ...options, ns });
          // Return the translation if found (doesn't return raw key)
          if (typeof result === 'string' && result !== key) {
            return result;
          }
        }
        return null;
      } catch {
        return null;
      }
    }, [i18n]);

    const t = useCallback((key: string, options?: any): string => {
      const stringKey = typeof key === 'string' ? key : String(key);

      // Return human-readable fallback if namespace is not ready yet
      // This prevents raw translation keys from showing during initial render
      if (!namespaceLoaded || !isClient) {
        return getHumanReadableFallback(stringKey, moduleName, options);
      }

      // Try to get translation even during SSR - modern i18next handles this better
      if (typeof moduleT !== 'function') {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation function not available for module: ${moduleName}`);
        }
        return getHumanReadableFallback(stringKey, moduleName, options);
      }

      // Debug logging disabled for successful translations to reduce console spam
      // Only errors and warnings will be logged below

      // Ensure function exists before calling it
      if (typeof moduleT !== 'function') {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation function not available for module: ${moduleName}`);
        }
        return getHumanReadableFallback(stringKey, moduleName, options);
      }

      try {
        const moduleTranslation = moduleT(stringKey, options);

        let result = moduleTranslation;

        // CRITICAL FIX: If translation returns the raw key (namespace not ready or key missing),
        // try common namespace as fallback before using human-readable fallback
        if (typeof result === 'string' && result === stringKey) {
          // Try common namespace fallback
          const commonResult = getCommonTranslation(stringKey, options);

          if (commonResult) {
            return commonResult;
          }

          // Still not found - use human-readable fallback
          if (process.env.NODE_ENV === 'development' && namespaceLoaded) {
            console.warn(`[i18n] Translation key not found: ${stringKey} (tried ${moduleName} and common), using fallback`);
          }
          return getHumanReadableFallback(stringKey, moduleName, options);
        }
        
        if (typeof result !== 'string') {
          if (result !== null && typeof result === 'object') {
            if (process.env.NODE_ENV === 'development') {
              console.error(`Translation returned OBJECT for key: ${stringKey} in module: ${moduleName}`, result);
            }
            
            if (Array.isArray(result)) {
              result = result
                .filter(item => item !== null && item !== undefined && typeof item !== 'object')
                .map(item => String(item))
                .join(' ') || stringKey;
            } else {
              try {
                result = safeReactChild(result);
                if (typeof result !== 'string' || result.length === 0) {
                  console.error(`safeReactChild failed for key: ${stringKey}, falling back to key`);
                  result = stringKey;
                }
              } catch (objError) {
                console.error(`Failed to convert object for translation key: ${stringKey}`, objError, result);
                result = stringKey;
              }
            }
          } else {
            result = result === null || result === undefined 
              ? stringKey 
              : String(result);
          }
        }
        
        if (typeof result === 'string' && result.trim().length > 0) {
          // Final check: if result looks like a raw translation key (contains dots and underscores, no spaces)
          // return human-readable fallback instead
          const looksLikeRawKey = /^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)+$/.test(result) &&
                                 !result.includes(' ');

          if (looksLikeRawKey) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`[i18n] Result looks like raw translation key: ${stringKey} -> ${result}, using fallback`);
            }
            return getHumanReadableFallback(stringKey, moduleName, options);
          }

          return String(result);
        }

        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation empty/invalid for key: ${stringKey}, returning fallback`);
        }
        return getHumanReadableFallback(stringKey, moduleName, options);
      } catch (error) {
        console.error(`Translation error for key: ${stringKey} in module: ${moduleName}`, error);
        return getHumanReadableFallback(stringKey, moduleName, options);
      }
    }, [moduleT, ready, namespaceLoaded, moduleName, i18n?.isInitialized, isClient, getCommonTranslation]);

    return {
      t,
      i18n: i18n || null,
      ready: namespaceLoaded && isClient,
      // Prevent hydration mismatch: only use actual language after client mount
      isRTL: isClient ? (i18n?.language === 'ar') : false,
      language: i18n?.language || 'en',
      changeLanguage: i18n?.changeLanguage || (() => {}),
      safeT,
    };
  } catch (error) {
    // Return safe fallback if hook fails
    if (process.env.NODE_ENV === 'development') {
      console.error(`Translation hook failed for module: ${moduleName}`, error);
    }

    const getHumanReadableFallback = (key: string, context?: string): string => {
      // Generic fallback: convert camelCase to Title Case
      const lastPart = key.split('.').pop() || key;
      return lastPart
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    };

    const fallbackT = (key: string): string => {
      const stringKey = typeof key === 'string' ? key : String(key);
      return getHumanReadableFallback(stringKey, moduleName, options);
    };
    return {
      t: fallbackT,
      i18n: null,
      ready: false,
      isRTL: false,
      language: 'en',
      changeLanguage: () => {},
      safeT: (content: any): string => String(content || ''), // Safe fallback
    };
  }
}

// Export as both useTranslation and useModuleTranslation for compatibility
export { useTranslation as useModuleTranslation };